import React, { ReactNode, RefObject, useMemo } from 'react';

import { Icon } from '@rbilabs/components-library/build/components/icon';
import { useIdCounter } from '@rbilabs/components-library/build/hooks/use-id-counter';
import { FieldProps } from 'formik';
import styled from 'styled-components';

import { IInputProps } from '@rbi-ctg/frontend';
import { IconCheck } from 'components/icons/check';
import { IconX } from 'components/icons/x';
import { FormValidationState } from 'utils/form';

import {
  FloatingLabelInputWrapper,
  IconContainerSelect,
  IconContainerSelectArrow,
  Label,
  Message,
  StyledInput,
} from './styled';
import { ISelectOption } from './types';
import { useInputHandles } from './use-input-handles';
import { useTestIds } from './use-test-ids';

const isMobile = /Mobile/.test(navigator.userAgent);

const StyledSelect = styled(StyledInput)<{ showIcon: boolean }>`
  && {
    padding-block: 0.5rem;
    padding-inline-start: ${p => (p.showIcon ? 2.2 : 1)}rem;
    padding-inline-end: 2.5rem;
  }
  cursor: pointer;
`;

const IconWrapper = styled.span`
  position: absolute;
  inset-block-start: 15px;
  inset-inline-start: 16px;
`;
interface IFloatingLabelSelectProps extends IInputProps<HTMLSelectElement> {
  autoComplete?: string;
  hasFooter?: boolean;
  footer?: ReactNode;
  label: string;
  labelId?: string;
  validation?: FormValidationState;
  message?: string;
  readOnly?: boolean;
  disabled?: boolean;
  options: ISelectOption[];
  min?: string;
  icon?: React.FC<{ height: string }>;
}

/** @deprecated Use Select from '@rbilabs/components-library' instead */
export const FloatingLabelSelect = React.forwardRef(
  (
    {
      'data-testid': testId,
      onBlur,
      onFocus,
      label,
      value,
      validation,
      message,
      onChange,
      className,
      autoComplete,
      hasFooter,
      footer,
      labelId,
      readOnly = false,
      disabled = false,
      options = [],
      icon,
      ...rest
    }: IFloatingLabelSelectProps,
    ref: RefObject<HTMLSelectElement>
  ) => {
    const inputId = useIdCounter('floating-label-select-input');

    const messageId = useIdCounter('message');

    const { handleBlur, handleChange, handleFocus } = useInputHandles<HTMLSelectElement>({
      inputId,
      isMobile,
      queryType: 'select',
      onBlur,
      onChange,
      onFocus,
    });

    const { wrapperTestId, labelTestId, validationTestId, messageTestId } = useTestIds(testId);

    const selectProps = useMemo(
      () => ({
        'aria-describedby': messageId,
        autoComplete,
        'data-testid': testId,
        hasFooter: !!hasFooter,
        id: inputId,
        onBlur: handleBlur,
        onChange: handleChange,
        onFocus: handleFocus,
        readOnly,
        disabled: readOnly || disabled,
        validation,
        value,
      }),
      [
        autoComplete,
        handleBlur,
        handleChange,
        handleFocus,
        inputId,
        messageId,
        hasFooter,
        readOnly,
        testId,
        validation,
        value,
        disabled,
      ]
    );

    // We want to make sure the floating label is not floating in the case
    // of a "placeholder" option, i.e., an option without a value but has
    // a label that we want to show to the user
    const selectedOptionHasLabel = options.some(o => value === o.value && o.label);

    const SelectIcon = icon || React.Fragment;
    const showIcon = !!icon && !value;

    return (
      <FloatingLabelInputWrapper className={className} data-testid={wrapperTestId}>
        {showIcon && (
          <IconWrapper>
            <SelectIcon height="20" />
          </IconWrapper>
        )}
        <StyledSelect
          as="select"
          ref={ref}
          data-private
          data-dd-privacy="mask"
          showIcon={showIcon}
          {...selectProps}
          {...rest}
        >
          {options.map(o => (
            <option key={`${o.value}+${o.label}`} value={o.value} disabled={o.disabled}>
              {o.label}
            </option>
          ))}
        </StyledSelect>
        {footer}
        <Label
          id={labelId}
          htmlFor={inputId}
          hasValue={!!value || selectedOptionHasLabel}
          data-testid={labelTestId}
        >
          {label}
        </Label>
        <IconContainerSelect validation={validation} data-testid={validationTestId}>
          {validation === FormValidationState.INVALID && (
            <IconX data-testid="input-validation-invalid" />
          )}
          {validation === FormValidationState.VALID && (
            <IconCheck data-testid="input-validation-valid" />
          )}
        </IconContainerSelect>
        {!disabled && (
          <IconContainerSelectArrow>
            <Icon icon="select" color="icon-default" width="24px" aria-hidden />
          </IconContainerSelectArrow>
        )}
        {message && (
          <Message id={messageId} data-testid={messageTestId} validation={validation}>
            {message}
          </Message>
        )}
      </FloatingLabelInputWrapper>
    );
  }
);

/** @deprecated Use FormikSelect instead */
export const FormikFloatingLabelSelect = ({
  field,
  ...props
}: FieldProps & IFloatingLabelSelectProps) => <FloatingLabelSelect {...field} {...props} />;
